import { makeStyles, shorthands, tokens } from '@fluentui/react-components';

/**
 * Log del proceso de sincronización.
 *
 * Jerarquía de la pantalla, de arriba hacia abajo:
 *
 *   1. Una sola tarjeta de controles (resumen + filtros). Antes eran cinco
 *      superficies flotantes —cuatro tarjetas de resumen y el panel de
 *      filtros—, todas con sombra y borde: nada resaltaba porque todo pesaba
 *      lo mismo, y entre las dos zonas se comían casi media pantalla.
 *   2. La lista, que es el contenido y por eso se queda con el espacio.
 *      Filas planas separadas por una línea, no tarjetas con sombra: un log se
 *      recorre de un vistazo y las sombras suman ruido sin agregar sentido.
 *   3. El paginado, fijo abajo.
 *
 * La elevación queda reservada para el estado: hover y fila abierta. Si todo
 * flota, la elevación deja de significar algo.
 */
export const useProtocolConsumptionSyncProcessLogStyles = makeStyles({
    // Cuerpo del GuideDrawer (padding de la guía): no scrollea; la lista tiene su
    // propio scroll y el paginado queda fijo abajo.
    drawerBody: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflowY: 'hidden',
        '& > div': {
            flexGrow: 1,
            minHeight: 0,
            rowGap: tokens.spacingVerticalM,
        },
    },

    // ── Zona de controles: resumen y filtros en una sola superficie ─────────
    controlsPanel: {
        flexShrink: 0,
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.borderRadius(tokens.borderRadiusLarge),
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
        ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalM,
    },

    // ── Resumen ────────────────────────────────────────────────────────────
    summaryStrip: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        columnGap: tokens.spacingHorizontalL,
        rowGap: tokens.spacingVerticalS,
    },
    summaryTotal: {
        display: 'flex',
        alignItems: 'baseline',
        columnGap: tokens.spacingHorizontalXS,
    },
    // El único número grande de la pantalla: es el total real del servidor y
    // el que ancla la lectura.
    summaryTotalValue: {
        color: tokens.colorNeutralForeground1,
        fontSize: tokens.fontSizeHero800,
        lineHeight: tokens.lineHeightHero800,
        fontWeight: tokens.fontWeightSemibold,
        fontVariantNumeric: 'tabular-nums',
    },
    summaryTotalLabel: {
        color: tokens.colorNeutralForeground2,
        fontSize: tokens.fontSizeBase300,
        lineHeight: tokens.lineHeightBase300,
    },
    summaryFilteredHint: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
    },

    // El desglose por severidad es de la página cargada, así que se dice una
    // sola vez arriba y los números van en cuerpo chico: son secundarios.
    breakdown: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalXXS,
        alignItems: 'flex-end',
    },
    breakdownCaption: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
    },
    breakdownItems: {
        display: 'flex',
        flexWrap: 'wrap',
        columnGap: tokens.spacingHorizontalM,
        rowGap: tokens.spacingVerticalXXS,
    },
    breakdownItem: {
        display: 'inline-flex',
        alignItems: 'center',
        columnGap: tokens.spacingHorizontalXS,
        color: tokens.colorNeutralForeground2,
        fontSize: tokens.fontSizeBase300,
        lineHeight: tokens.lineHeightBase300,
        fontVariantNumeric: 'tabular-nums',
    },
    breakdownCount: {
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    dot: {
        width: '8px',
        height: '8px',
        ...shorthands.borderRadius('50%'),
        flexShrink: 0,
    },
    dotInfo: { backgroundColor: tokens.colorBrandBackground },
    dotWarning: { backgroundColor: tokens.colorPaletteMarigoldBorderActive },
    dotError: { backgroundColor: tokens.colorPaletteRedBorderActive },

    divider: {
        height: '1px',
        backgroundColor: tokens.colorNeutralStroke2,
    },

    // ── Filtros ────────────────────────────────────────────────────────────
    filtersBlock: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalS,
    },
    // Los dos campos de texto comparten fila mientras el ancho lo permita.
    filtersInputs: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        columnGap: tokens.spacingHorizontalM,
        rowGap: tokens.spacingVerticalS,
    },
    filterField: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalXXS,
        minWidth: 0,
    },
    filterGroup: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: tokens.spacingHorizontalS,
        rowGap: tokens.spacingVerticalXS,
    },
    filterLabel: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
        fontWeight: tokens.fontWeightSemibold,
    },
    // Ancho mínimo para que la etiqueta no empuje a los chips y las dos filas
    // de filtros arranquen alineadas.
    filterGroupLabel: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
        fontWeight: tokens.fontWeightSemibold,
        minWidth: '68px',
    },
    // Envuelven en varias líneas: antes scrolleaban en horizontal y las
    // opciones del final quedaban escondidas sin ninguna señal.
    filterChips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: tokens.spacingHorizontalXS,
        minWidth: 0,
    },
    filterChip: {
        minWidth: 'fit-content',
        whiteSpace: 'nowrap',
    },
    filterActions: {
        display: 'flex',
        justifyContent: 'flex-end',
    },

    // ── Lista ──────────────────────────────────────────────────────────────
    scrollSection: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.borderRadius(tokens.borderRadiusLarge),
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    },
    // Recarga en curso sobre una lista que ya tiene contenido: se atenúa y deja
    // de aceptar clics, pero no se vacía.
    scrollSectionBusy: {
        opacity: 0.55,
        pointerEvents: 'none',
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
    },

    item: {
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: '3px minmax(0, 1fr)',
        alignItems: 'stretch',
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
        transitionDuration: '120ms',
        transitionProperty: 'background-color',
        transitionTimingFunction: 'ease',
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        // El foco de teclado tiene que verse igual que el hover, o navegar con
        // Tab deja de indicar dónde se está parado.
        ':focus-within': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        ':last-child': {
            ...shorthands.borderBottom('0', 'solid', 'transparent'),
        },
        '@media (prefers-reduced-motion: reduce)': {
            transitionDuration: '0ms',
        },
    },
    itemExpanded: {
        backgroundColor: tokens.colorNeutralBackground1Selected,
    },

    itemAction: {
        appearance: 'none',
        backgroundColor: 'transparent',
        ...shorthands.border('0', 'solid', 'transparent'),
        cursor: 'pointer',
        inset: 0,
        ...shorthands.margin(0),
        ...shorthands.padding(0),
        position: 'absolute',
        zIndex: 3,
        ':focus-visible': {
            ...shorthands.outline('2px', 'solid', tokens.colorBrandStroke1),
            outlineOffset: '-2px',
        },
    },
    srOnly: {
        position: 'absolute',
        width: '1px',
        height: '1px',
        ...shorthands.padding(0),
        ...shorthands.margin('-1px'),
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        ...shorthands.border('0', 'solid', 'transparent'),
    },

    // La barra de severidad es el ancla de color de la fila: reemplaza a la
    // sombra como forma de distinguir un evento del siguiente.
    itemAccent: {
        minHeight: '100%',
    },
    itemAccentInfo: { backgroundColor: tokens.colorBrandBackground },
    itemAccentWarning: { backgroundColor: tokens.colorPaletteMarigoldBorderActive },
    itemAccentError: { backgroundColor: tokens.colorPaletteRedBorderActive },

    itemMain: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    // La zona pulsable: sólo la cabecera y el resumen. El detalle queda afuera
    // para que su texto se pueda seleccionar.
    itemHeaderZone: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalXXS,
        minWidth: 0,
        cursor: 'pointer',
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    },
    itemHeader: {
        display: 'flex',
        alignItems: 'center',
        columnGap: tokens.spacingHorizontalS,
        minWidth: 0,
    },
    itemBadge: {
        flexShrink: 0,
    },
    // Tipo y paciente en texto atenuado en vez de insignias: tres insignias
    // seguidas dejaban al título sin ancho y lo truncaban de entrada.
    itemInlineMeta: {
        display: 'inline-flex',
        alignItems: 'center',
        columnGap: tokens.spacingHorizontalXS,
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        overflow: 'hidden',
    },
    // Se queda con el espacio sobrante y es lo único que se recorta: el resto
    // de la fila tiene ancho fijo.
    itemTitle: {
        flexGrow: 1,
        flexShrink: 1,
        color: tokens.colorNeutralForeground1,
        fontSize: tokens.fontSizeBase300,
        lineHeight: tokens.lineHeightBase300,
        fontWeight: tokens.fontWeightSemibold,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
    },
    // La hora manda; la fecha completa vive en el title del elemento. En un log
    // casi todos los eventos son del mismo día y repetirla cien veces es ruido.
    itemTime: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
        flexShrink: 0,
    },
    itemPreviewText: {
        color: tokens.colorNeutralForeground2,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase300,
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: '1',
        overflow: 'hidden',
    },

    // Afordancia explícita de "esto se abre": antes la única señal era el
    // cursor, y con un botón invisible encima no había nada que mirar.
    itemChevron: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: tokens.colorNeutralForeground3,
        transitionDuration: '150ms',
        transitionProperty: 'transform',
        transitionTimingFunction: 'ease',
        '@media (prefers-reduced-motion: reduce)': {
            transitionDuration: '0ms',
        },
    },
    itemChevronOpen: {
        transform: 'rotate(90deg)',
    },

    // Se abre con grid en vez de max-height: un mensaje largo ya no queda
    // recortado en silencio a una altura fija.
    itemExpandedContent: {
        display: 'grid',
        gridTemplateRows: '0fr',
        transitionDuration: '180ms',
        transitionProperty: 'grid-template-rows',
        transitionTimingFunction: 'ease',
        '@media (prefers-reduced-motion: reduce)': {
            transitionDuration: '0ms',
        },
    },
    itemExpandedContentOpen: {
        gridTemplateRows: '1fr',
    },
    itemExpandedInner: {
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalS,
    },
    // El detalle está fuera de la zona pulsable: cursor normal y texto
    // seleccionable, para poder copiar un mensaje de error o un ID.
    itemExpandedPadding: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalS,
        cursor: 'auto',
        userSelect: 'text',
        ...shorthands.padding('0', tokens.spacingHorizontalM, tokens.spacingVerticalM, tokens.spacingHorizontalM),
    },

    detailSection: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalXXS,
    },
    itemMessage: {
        color: tokens.colorNeutralForeground2,
        fontSize: tokens.fontSizeBase300,
        lineHeight: tokens.lineHeightBase400,
        whiteSpace: 'pre-wrap',
    },
    itemDetailsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: tokens.spacingHorizontalS,
    },
    detailPill: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalXXS,
        backgroundColor: tokens.colorNeutralBackground2,
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
        minWidth: 0,
    },
    detailLabel: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase100,
        lineHeight: tokens.lineHeightBase200,
        fontWeight: tokens.fontWeightSemibold,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
    },
    detailValue: {
        color: tokens.colorNeutralForeground1,
        fontSize: tokens.fontSizeBase300,
        lineHeight: tokens.lineHeightBase300,
        overflowWrap: 'anywhere',
    },
    // La sugerencia es lo accionable del evento: se distingue con un borde de
    // color en vez de quedar igual que el resto de los recuadros.
    suggestionBox: {
        backgroundColor: tokens.colorNeutralBackground2,
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        ...shorthands.borderLeft('3px', 'solid', tokens.colorBrandStroke1),
        ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalXXS,
    },
    suggestionLabel: {
        color: tokens.colorBrandForeground1,
        fontSize: tokens.fontSizeBase100,
        lineHeight: tokens.lineHeightBase200,
        fontWeight: tokens.fontWeightSemibold,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
    },

    // ── Estados y paginado ─────────────────────────────────────────────────
    centeredState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        minHeight: '200px',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        rowGap: tokens.spacingVerticalS,
        flex: 1,
        minHeight: '200px',
        ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalL),
        color: tokens.colorNeutralForeground3,
        textAlign: 'center',
    },
    paginationSection: {
        flexShrink: 0,
    },
});
